import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Search, Home, Phone, Mail, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/seo/SEO";
import { useState } from "react";

const NotFound = () => {
  const [searchQuery, setSearchQuery] = useState("");

  const popularPages = [
    { title: "Zonnepanelen", href: "/zonnepanelen", description: "Hoogrendement zonnepanelen installatie" },
    { title: "Warmtepompen", href: "/warmtepompen", description: "All electric warmtepomp met subsidie" },
    { title: "Thuisbatterijen", href: "/thuisbatterijen", description: "Energieopslag voor zelfvoorzienendheid" },
    { title: "Laadpalen", href: "/laadpalen", description: "Slimme laadoplossingen voor EV" },
    { title: "Smart Home", href: "/smart-home", description: "Loxone domotica systemen" },
    { title: "Contact", href: "/contact", description: "Neem contact met ons op" }
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Simple search redirect - in a real app you'd implement proper search
      const lowercaseQuery = searchQuery.toLowerCase();
      if (lowercaseQuery.includes('zonnepaneel') || lowercaseQuery.includes('solar')) {
        window.location.href = '/zonnepanelen';
      } else if (lowercaseQuery.includes('warmtepomp') || lowercaseQuery.includes('heat pump')) {
        window.location.href = '/warmtepompen';
      } else if (lowercaseQuery.includes('batterij') || lowercaseQuery.includes('battery')) {
        window.location.href = '/thuisbatterijen';
      } else if (lowercaseQuery.includes('laadpaal') || lowercaseQuery.includes('charging')) {
        window.location.href = '/laadpalen';
      } else if (lowercaseQuery.includes('smart home') || lowercaseQuery.includes('domotica')) {
        window.location.href = '/smart-home';
      } else {
        window.location.href = '/contact';
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <SEO
          title="Pagina Niet Gevonden | 404 Error | SOL-VI"
          description="Deze pagina bestaat niet meer. Vind wat u zoekt via onze navigatie of neem contact op voor hulp met duurzame energieoplossingen."
          canonical="https://sol-vi.nl/404"
          type="website"
        />
        
        <div className="pt-20 pb-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              
              {/* Error Message */}
              <div className="mb-12">
                <div className="text-8xl font-bold text-primary/20 mb-4">404</div>
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  Pagina Niet Gevonden
                </h1>
                <p className="text-xl text-muted-foreground mb-8">
                  De pagina die u zoekt bestaat niet of is verplaatst. Geen zorgen, 
                  we helpen u graag verder met onze duurzame energieoplossingen.
                </p>
              </div>

              {/* Search */}
              <Card className="mb-12 max-w-lg mx-auto">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Search className="h-5 w-5" />
                    Zoeken
                  </CardTitle>
                  <CardDescription>
                    Zoek naar de informatie die u nodig heeft
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSearch} className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Zoek naar zonnepanelen, warmtepompen..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <Button type="submit" size="sm">
                      Zoeken
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Popular Pages */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold mb-8">Populaire Pagina's</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {popularPages.map((page, index) => (
                    <Card key={index} className="hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <CardTitle className="text-lg">
                          <Link 
                            to={page.href}
                            className="hover:text-primary transition-colors flex items-center gap-2"
                          >
                            {page.title}
                            <ArrowRight className="h-4 w-4" />
                          </Link>
                        </CardTitle>
                        <CardDescription>{page.description}</CardDescription>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="grid md:grid-cols-3 gap-6 mb-12">
                <Card className="p-6 text-center">
                  <Home className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Terug naar Home</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Ontdek al onze duurzame energieoplossingen
                  </p>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/">
                      Naar Homepage
                    </Link>
                  </Button>
                </Card>

                <Card className="p-6 text-center">
                  <Phone className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Bel Direct</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Spreek direct met onze experts
                  </p>
                  <Button asChild variant="outline" size="sm">
                    <a href="tel:+31481749999">
                      026 389 0918
                    </a>
                  </Button>
                </Card>

                <Card className="p-6 text-center">
                  <Mail className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Email Contact</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Stuur ons een bericht
                  </p>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/contact">
                      Contact Formulier
                    </Link>
                  </Button>
                </Card>
              </div>

              {/* Final CTA */}
              <div className="bg-muted/50 rounded-lg p-8">
                <h2 className="text-2xl font-bold mb-4">
                  Hulp nodig met duurzame energie?
                </h2>
                <p className="text-muted-foreground mb-6">
                  Onze experts staan klaar om u te helpen met zonnepanelen, warmtepompen, 
                  thuisbatterijen en meer.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    variant="hero" 
                    size="lg"
                    onClick={() => (window as any).Calendly?.initPopupWidget?.({
                      url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800'
                    })}
                  >
                    Plan Gratis Adviesgesprek
                  </Button>
                  <Button variant="outline" size="lg" asChild>
                    <Link to="/contact">
                      <Mail className="mr-2 h-4 w-4" />
                      Neem Contact Op
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default NotFound;